 import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Save, ArrowLeft, Image as ImageIcon } from 'lucide-react';

const UpdateBlog = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);

    // Form States
    const [title, setTitle] = useState('');
    const [author, setAuthor] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [content, setContent] = useState('');
    const [date, setDate] = useState('');
    const [newImage, setNewImage] = useState(null);
    const [preview, setPreview] = useState('');

    useEffect(() => {
        const fetchBlogDetails = async () => {
            try {
                const token = localStorage.getItem('adminToken');
                const res = await axios.get(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/viewblog/${id}`, {
                    headers: { authorization: `Bearer ${token}` }
                });
                const b = res.data.blog;
                setTitle(b.title);
                setAuthor(b.author);
                setExcerpt(b.excerpt);
                setContent(b.content);
                // Formatting date for the input field (YYYY-MM-DD)
                setDate(new Date(b.date).toISOString().split('T')[0]);
                setPreview(b.image);
            } catch (err) {
                alert("Error fetching blog details");
                navigate('/admin/blogs');
            } finally {
                setLoading(false);
            }
        };
        fetchBlogDetails();
    }, [id, navigate]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setNewImage(file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setUpdating(true);
        const formData = new FormData();
        formData.append('title', title);
        formData.append('author', author);
        formData.append('excerpt', excerpt);
        formData.append('content', content);
        formData.append('date', date);
        if (newImage) formData.append('image', newImage);

        try {
            const token = localStorage.getItem('adminToken');
            await axios.put(`${process.env.REACT_APP_API_URL}/api/adminworkspace001marvelsato/marvel-sato-management/updateblog/${id}`, formData, {
                headers: { 
                    'Content-Type': 'multipart/form-data',
                    authorization: `Bearer ${token}` 
                }
            });
            alert("Blog updated successfully!");
            navigate('/admin/blogs');
        } catch (err) {
            alert(err.message);
        } finally {
            setUpdating(false);
        }
    };

    if (loading) return <div className="h-screen flex items-center justify-center"><Loader2 className="animate-spin text-orange-500" /></div>;

    return (
        <div className="p-8 max-w-5xl mx-auto space-y-8">
            <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-[10px] font-black uppercase text-slate-400 hover:text-orange-500 transition-all">
                <ArrowLeft size={14} /> Back to Editorial
            </button>

            <h1 className="text-3xl font-black uppercase italic">Edit <span className="text-orange-500">Post</span></h1>

            <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* LEFT: MAIN CONTENT */}
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400">Post Title</label>
                            <input type="text" value={title} onChange={(e)=>setTitle(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl font-bold text-lg" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400">Content</label>
                            <textarea value={content} onChange={(e)=>setContent(e.target.value)} className="w-full p-4 bg-slate-50 rounded-2xl h-96 text-sm leading-relaxed" />
                        </div>
                    </div>
                </div>

                {/* RIGHT: METADATA & IMAGE */}
                <div className="space-y-6">
                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400">Author</label>
                            <input type="text" value={author} onChange={(e)=>setAuthor(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400">Date</label>
                            <input type="date" value={date} onChange={(e)=>setDate(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl font-bold" />
                        </div>
                        <div className="space-y-1">
                            <label className="text-[10px] font-black uppercase text-slate-400">Short Excerpt</label>
                            <textarea value={excerpt} onChange={(e)=>setExcerpt(e.target.value)} className="w-full p-3 bg-slate-50 rounded-xl h-24 text-xs" />
                        </div>
                    </div>

                    <div className="bg-white p-6 rounded-3xl border border-slate-100 shadow-sm space-y-4">
                        <label className="text-[10px] font-black uppercase text-slate-400">Cover Image</label>
                        <div className="relative aspect-video rounded-2xl overflow-hidden bg-slate-100 group">
                            <img src={preview} className="w-full h-full object-cover" alt="preview" />
                            <label className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer text-white text-[10px] font-black uppercase gap-2">
                                <ImageIcon size={16}/> Change Image
                                <input type="file" className="hidden" onChange={handleImageChange} />
                            </label>
                        </div>
                    </div>

                    <button type="submit" disabled={updating} className="w-full py-4 bg-orange-500 text-white font-black uppercase rounded-2xl shadow-xl shadow-orange-500/20 hover:bg-slate-900 transition-all flex justify-center items-center gap-2">
                        {updating ? <Loader2 className="animate-spin" /> : <><Save size={18}/> Save Changes</>}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default UpdateBlog;